/**
 * "IT'S YOUR TURN" — the head-to-head games waiting on this player (§ F179).
 *
 * Jake, 2026-09-18: *"When logging in, if there is a head-to-head that needs my
 * turn, I should be prompted after login with the option to go straight to that
 * event. If there is more than one then I should be able to pick."* — written
 * the morning after he sent MUMMY a Wordle on prod and she never saw it.
 *
 * A turn-based game with no push notifications lives or dies on the app TELLING
 * you, because the whole premise is that the other person is not there when
 * they move. Two places do the telling, and they share this file so they cannot
 * disagree about what "waiting on you" means:
 *
 *   `YourTurnPrompt`  after "Who Are You?" — the one moment everybody passes
 *                     through. Every waiting game is a button (so "more than
 *                     one" IS the picker), and "Not now" carries on to wherever
 *                     the sign-in was headed.
 *   `useMyTurns`      the count on the hub's Head To Head door and on the
 *                     category menu — because an identity is remembered for
 *                     weeks (§ F117), so most visits never pass "Who Are You?"
 *                     at all, and a prompt that only appears at sign-in would
 *                     be a prompt Mummy saw once.
 *
 * "Waiting on you" is the server's `my_turn` on an ACTIVE match — a word to
 * guess or a word to think of — and nothing else. It is never recomputed here.
 */

import { useEffect, useState } from "react";
import { fetchMatches } from "./api";
import { h2hMatchPath } from "./paths";
import { h2hGameName } from "./games";
import type { WireMatchSummary } from "./types";
import type { StoredPlayer } from "../lib/player";

/** The active matches whose next move is this player's. Pure; exported for tests. */
export function waitingOn(matches: readonly WireMatchSummary[]): WireMatchSummary[] {
  return matches.filter((m) => m.status === "active" && m.my_turn);
}

/** What one waiting game asks of you, in the row's own words. */
export function turnAsk(match: WireMatchSummary): string {
  // § F198 — a Doodle turn is a drawing, not a word.
  if (match.game_id === "doodle") {
    return match.action === "guess"
      ? `${match.opponent_name} sent you a drawing`
      : `Your turn to draw for ${match.opponent_name}`;
  }
  return match.action === "guess"
    ? `${match.opponent_name} sent you a word`
    : `Your turn to send ${match.opponent_name} a word`;
}

/** Ask the server what is waiting. Never throws: no answer is "nothing waiting". */
export async function fetchMyTurns(player: StoredPlayer): Promise<WireMatchSummary[]> {
  try {
    return waitingOn((await fetchMatches(player)).matches);
  } catch {
    return [];
  }
}

/** The waiting games for whoever is signed in; empty for nobody. */
export function useMyTurns(player: StoredPlayer | null): WireMatchSummary[] {
  const [turns, setTurns] = useState<WireMatchSummary[]>([]);
  const id = player?.id ?? null;
  const token = player?.token ?? null;
  useEffect(() => {
    if (!id || !token) {
      setTurns([]);
      return;
    }
    let live = true;
    const load = () => void fetchMyTurns({ id, name: "", token }).then((t) => live && setTurns(t));
    load();
    // A restored tab (iOS) re-runs no mount effect — § F79's lesson.
    const wake = () => document.visibilityState === "visible" && load();
    document.addEventListener("visibilitychange", wake);
    window.addEventListener("pageshow", wake);
    return () => {
      live = false;
      document.removeEventListener("visibilitychange", wake);
      window.removeEventListener("pageshow", wake);
    };
  }, [id, token]);
  return turns;
}

/**
 * The number a door wears when something is waiting. Nothing when nothing is.
 * § F197 — Jake: *"head to head should have a number indicator reflecting how
 * many games are waiting on my turn."* A red count bubble — the same signal a
 * phone's app icon uses — rather than words.
 */
export function YourTurnBadge({ count, testId }: { count: number; testId: string }) {
  if (count <= 0) return null;
  return (
    <span
      data-testid={testId}
      data-waiting={count}
      aria-label={`${count} ${count === 1 ? "game" : "games"} waiting on your turn`}
      className="shrink-0 min-w-[1.75rem] h-7 px-1.5 rounded-full bg-brand-secondary text-white ink-border font-heading font-extrabold text-sm leading-none flex items-center justify-center tabular-nums"
    >
      {count}
    </span>
  );
}

export function YourTurnPrompt({
  turns,
  onGo,
  onSkip,
}: {
  turns: readonly WireMatchSummary[];
  /** Go straight to a game. */
  onGo(path: string): void;
  /** Carry on to wherever the sign-in was headed. */
  onSkip(): void;
}) {
  return (
    <div data-testid="your-turn-prompt" data-count={turns.length} className="flex flex-col gap-3">
      <h1 className="font-heading font-extrabold text-2xl tracking-tight text-center">
        {turns.length === 1 ? "It's your turn" : "Your turn in a few"}
      </h1>
      <div className="flex flex-col gap-2">
        {turns.map((m) => (
          <button
            key={m.id}
            type="button"
            data-testid={`your-turn-${m.id}`}
            onClick={() => onGo(h2hMatchPath(m.id, m.game_id))}
            className="w-full min-h-[44px] bg-brand-quaternary ink-border rounded-block block-shadow-1 btn-press px-3 py-2 text-left"
          >
            <span className="font-heading font-extrabold uppercase tracking-tight text-base leading-tight block">
              {h2hGameName(m.game_id)} · {m.opponent_name}
            </span>
            <span className="font-body text-xs text-black/70 block">{turnAsk(m)}</span>
          </button>
        ))}
      </div>
      <button
        type="button"
        data-testid="your-turn-skip"
        onClick={onSkip}
        className="w-full min-h-[44px] bg-white font-heading font-extrabold uppercase tracking-wide text-sm ink-border rounded-block btn-press text-black/70"
      >
        Not now
      </button>
    </div>
  );
}
